import React from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  BookOpen,
  Briefcase,
  Home,
  MessageCircle,
  Shield,
} from "lucide-react";
import Reveal from "../components/ui/Reveal";
import IconBox from "../components/ui/IconBox";
import SEO, { generateBreadcrumbSchema, generateWebPageSchema } from "../components/seo/SEO";

const benefits = [
  {
    icon: BarChart3,
    title: "Student Engagement Analytics",
    description: "Track student activity, study patterns, and engagement metrics across your institution in real-time dashboards.",
  },
  {
    icon: MessageCircle,
    title: "Official Communication Channel",
    description: "Reach students through verified institutional channels. Announcements sync directly to student timetables and calendars.",
  },
  {
    icon: BookOpen,
    title: "Course Content Integration",
    description: "Upload official course outlines and materials. Students get auto-generated study aids aligned with your curriculum.",
  },
  {
    icon: Home,
    title: "Hostel Management",
    description: "Integrate hostel inventory and booking directly into the student app. Streamline accommodation allocation.",
  },
  {
    icon: Briefcase,
    title: "Career Services Integration",
    description: "Post internships and opportunities directly to relevant students. Track applications and placements.",
  },
  {
    icon: Shield,
    title: "Verified Student Identity",
    description: "Digital student ID integration for secure verification at events, libraries, and campus facilities.",
  },
];

const useCases = [
  {
    title: "For Universities",
    description: "Adopt CampusGenie as your official student app and provide a unified experience for academic life, campus services, and communication.",
    features: [
      "Branded instance with your colors and logo",
      "Integration with student information systems",
      "Official announcement and timetable channels",
      "Analytics dashboard for student affairs",
    ],
  },
  {
    title: "For Secondary Schools",
    description: "Help your students prepare for JAMB and WAEC with a dedicated prep platform that transitions them smoothly to university.",
    features: [
      "School-wide progress tracking",
      "Class-based leaderboards and challenges",
      "Teacher oversight dashboard",
      "Parent progress reports",
    ],
  },
  {
    title: "For EdTech Partners",
    description: "Integrate your content and services into the CampusGenie ecosystem to reach thousands of engaged students.",
    features: [
      "Content partnership opportunities",
      "API integration options",
      "Co-branded features",
      "Revenue sharing models",
    ],
  },
];

const pricingPlans = [
  {
    name: "Starter",
    price: "Free",
    description: "For schools exploring CampusGenie",
    features: [
      "Up to 500 students",
      "Basic analytics dashboard",
      "Standard support",
      "Community features",
    ],
    cta: "Get Started",
    featured: false,
  },
  {
    name: "Institution",
    price: "Custom",
    description: "For universities and large schools",
    features: [
      "Unlimited students",
      "Full analytics suite",
      "Priority support",
      "Custom branding",
      "API access",
      "Dedicated success manager",
    ],
    cta: "Contact Sales",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "For multi-campus deployments",
    features: [
      "Everything in Institution",
      "Multi-campus support",
      "SIS integration",
      "Custom development",
      "On-premise option",
      "SLA guarantee",
    ],
    cta: "Contact Sales",
    featured: false,
  },
];

export default function ForInstitutionsPage() {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "For Institutions", url: "/institutions" },
  ];

  const structuredData = [
    generateWebPageSchema({
      title: "For Institutions - CampusGenie",
      description: "Partner with CampusGenie to enhance student engagement, track analytics, and integrate with your institution's systems.",
      url: "/institutions",
    }),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title="For Institutions - Student Engagement & Analytics Platform"
        description="Partner with CampusGenie to enhance student engagement, track real-time analytics, integrate course content, and provide students with AI-powered learning tools. Solutions for universities, secondary schools, and EdTech partners."
        url="/institutions"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="institutions-hero">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">For Institutions</span>
              <h1 className="heading-hero" style={{ maxWidth: "16ch", margin: "0 auto 24px", color: "white" }}>
                Empower your students with a world-class platform.
              </h1>
              <p className="body-large" style={{ maxWidth: "600px", margin: "0 auto 32px" }}>
                Partner with CampusGenie to provide your students with comprehensive academic support, streamline campus operations, and gain insights into student engagement.
              </p>
              <div className="flex gap-4" style={{ justifyContent: "center" }}>
                <Link to="/contact" className="btn btn-primary btn-lg">
                  Schedule a Demo
                </Link>
                <a href="#pricing" className="btn btn-secondary btn-lg" style={{ 
                  background: "rgba(255,255,255,0.1)", 
                  borderColor: "rgba(255,255,255,0.2)",
                  color: "white"
                }}>
                  View Pricing
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="section section-dark" style={{ paddingTop: "64px" }}>
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Platform Benefits</span>
              <h2 className="heading-2" style={{ color: "white" }}>
                What CampusGenie brings to your institution.
              </h2>
            </div>
          </Reveal>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={index * 0.1}>
                <div className="benefit-card">
                  <div className="benefit-icon">
                    <IconBox icon={benefit.icon} size={28} />
                  </div>
                  <h3 className="heading-4">{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Use Cases</span>
              <h2 className="heading-2">
                Tailored solutions for every institution.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-3">
            {useCases.map((useCase, index) => (
              <Reveal key={useCase.title} delay={index * 0.1}>
                <div className="feature-card" style={{ height: "100%" }}>
                  <h3 className="heading-4" style={{ marginBottom: "12px" }}>{useCase.title}</h3>
                  <p className="body-small" style={{ marginBottom: "20px" }}>{useCase.description}</p>
                  <ul style={{ margin: 0 }}>
                    {useCase.features.map((feature) => (
                      <li key={feature} style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "8px",
                        padding: "8px 0",
                        fontSize: "14px",
                        color: "var(--color-text-secondary)"
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">1K+</div>
                <div className="stat-label">Students using CampusGenie</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">4-7x</div>
                <div className="stat-label">Higher retention than typical apps</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">90%</div>
                <div className="stat-label">Student satisfaction rate</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">24/7</div>
                <div className="stat-label">AI support availability</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section className="section pricing-section" id="pricing">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Pricing</span>
              <h2 className="heading-2">
                Plans that scale with your institution.
              </h2>
              <p className="body-large">
                Start free and upgrade as you grow. All plans include core features.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-3">
            {pricingPlans.map((plan, index) => (
              <Reveal key={plan.name} delay={index * 0.1}>
                <div className={`pricing-card ${plan.featured ? "featured" : ""}`}>
                  {plan.featured && <div className="pricing-badge">Most Popular</div>}
                  <h3 className="heading-4">{plan.name}</h3>
                  <div className="pricing-price">
                    {plan.price}
                    {plan.price !== "Free" && plan.price !== "Custom" && <span>/mo</span>}
                  </div>
                  <p className="body-small">{plan.description}</p>
                  <div className="pricing-features">
                    {plan.features.map((feature) => (
                      <div key={feature} className="pricing-feature">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/contact" 
                    className={`btn ${plan.featured ? "btn-primary" : "btn-secondary"}`}
                    style={{ width: "100%" }}
                  >
                    {plan.cta}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="section-heading section-heading-center">
              <span className="eyebrow">Implementation</span>
              <h2 className="heading-2">
                Getting started is simple.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-4">
            {[
              { step: "01", title: "Discovery Call", desc: "We learn about your institution's needs and goals." },
              { step: "02", title: "Custom Setup", desc: "We configure CampusGenie for your institution." },
              { step: "03", title: "Pilot Launch", desc: "Roll out to a pilot group of students and staff." },
              { step: "04", title: "Full Deployment", desc: "Scale across your entire institution." },
            ].map((item, index) => (
              <Reveal key={item.step} delay={index * 0.1}>
                <div className="feature-card" style={{ textAlign: "center" }}>
                  <div style={{ 
                    width: 48, 
                    height: 48, 
                    background: "var(--gradient-primary)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    margin: "0 auto 16px"
                  }}>
                    {item.step}
                  </div>
                  <h4 className="heading-4">{item.title}</h4>
                  <p className="body-small">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="cta-section">
              <div className="cta-content">
                <h2 className="heading-2">
                  Ready to transform your institution?
                </h2>
                <p className="body-large">
                  Join forward-thinking institutions using CampusGenie to support student success.
                </p>
                <div className="cta-buttons">
                  <Link to="/contact" className="btn btn-primary btn-lg">
                    Schedule a Demo
                  </Link>
                  <Link to="/product" className="btn btn-secondary btn-lg">
                    View Product Details
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
